import React, { useState, useEffect } from 'react';
import { View, FlatList, StyleSheet, TouchableOpacity, Image, RefreshControl } from 'react-native';
import { Text, Card, FAB, Searchbar, useTheme, ActivityIndicator, Chip, Button, Dialog, Portal, Paragraph, Snackbar } from 'react-native-paper';
import { getItemsByCategory, deleteMenuItem } from '../../../api/adminAPI';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const MenuItemListScreen = ({ route, navigation }) => {
  const theme = useTheme();
  const { categoryId, categoryName } = route.params || {};
  
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      
      if (!categoryId) {
        setSnackbarMessage('Geçersiz kategori ID');
        setSnackbarVisible(true);
        setLoading(false);
        return;
      }
      
      const response = await getItemsByCategory(categoryId);
      
      if (response.success) {
        // Frontend'de büyük/küçük harfe duyarsız alfabetik sıralama yap
        const sortedItems = [...(response.data || [])].sort((a, b) => 
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
        
        setMenuItems(sortedItems);
        setFilteredItems(sortedItems);
      } else {
        setSnackbarMessage('Menü öğeleri alınırken bir hata oluştu');
        setSnackbarVisible(true);
      }
    } catch (error) {
      console.error('Error fetching menu items:', error);
      setSnackbarMessage('Menü öğeleri alınırken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // ilk yükleme
    fetchMenuItems();
    
    // Ekrana her dönüldüğünde yeniden yükle
    const unsubscribe = navigation.addListener('focus', () => {
      fetchMenuItems();
    });
    
    return unsubscribe;
  }, [navigation, categoryId]);

  // Arama yapma
  useEffect(() => {
    if (searchQuery) {
      const filtered = menuItems.filter(
        item => item.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredItems(filtered);
    } else {
      setFilteredItems(menuItems);
    }
  }, [searchQuery, menuItems]);

  const onRefresh = React.useCallback(() => {
    setRefreshing(true);
    fetchMenuItems();
  }, []);

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setDeleteDialogVisible(true);
  };

  const handleDeleteItem = async () => {
    try {
      if (!itemToDelete) return;
      
      const response = await deleteMenuItem(itemToDelete.id);
      
      if (response.success) {
        // Menü öğesini listeden kaldır
        setMenuItems(prevItems => 
          prevItems.filter(item => item.id !== itemToDelete.id)
        );
        setSnackbarMessage('Menü öğesi başarıyla silindi');
      } else {
        setSnackbarMessage('Menü öğesi silinirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error deleting menu item:', error);
      setSnackbarMessage('Menü öğesi silinirken bir hata oluştu');
    } finally {
      setDeleteDialogVisible(false);
      setItemToDelete(null);
      setSnackbarVisible(true);
    }
  };

  const navigateToEditItem = (item) => {
    navigation.navigate('MenuItemForm', { item, categoryId });
  };

  const formatPrice = (price) => {
    // Fiyat değeri sayı değilse veya tanımsızsa
    if (price === null || price === undefined || isNaN(parseFloat(price))) {
      return '- ₺';
    }
    // Sayı ise parseFloat ile dönüştür ve formatla
    return `${parseFloat(price).toFixed(2)} ₺`;
  };

  const renderMenuItem = ({ item }) => (
    <Card style={styles.card}>
      <Card.Content>
        <View style={styles.itemHeader}>
          {item.image_url ? (
            <Image source={{ uri: item.image_url }} style={styles.itemImage} />
          ) : (
            <View style={styles.imagePlaceholder}>
              <MaterialCommunityIcons name="food" size={30} color="#BDBDBD" />
            </View>
          )}
          
          <View style={styles.itemInfo}>
            <Text style={styles.itemName}>{item.name}</Text>
            <Text style={styles.itemPrice}>{formatPrice(item.price)}</Text>
            
            <Chip 
  icon="circle" 
  style={[styles.statusChip, { backgroundColor: item.is_available ? 'rgba(125, 76, 236, 0.2)' : 'rgba(125, 76, 236, 0.05)' }]}
  textStyle={{color: 'rgb(125, 76, 236)', fontWeight: 'bold'}}
>
  {item.is_available ? 'Aktif' : 'Pasif'}
</Chip>
          </View>
        </View>
        
        {item.description && (
          <Text style={styles.itemDescription} numberOfLines={2}>
            {item.description}
          </Text>
        )}
        
        <View style={styles.itemActions}>
          <Button
            mode="outlined"
            style={[styles.actionButton, styles.editButton, {borderColor: 'rgb(125, 76, 236)'}]}
            icon="pencil"
            onPress={() => navigateToEditItem(item)}
            labelStyle={{ fontSize: 12, color: 'rgb(125, 76, 236)' }}
            compact
            textColor="rgb(125, 76, 236)"
          >
            Düzenle
          </Button>
          
          <Button
            mode="outlined"
            style={[styles.actionButton, {borderColor: 'rgb(125, 76, 236)'}]}
            icon="delete"
            onPress={() => confirmDelete(item)}
            labelStyle={{ fontSize: 12, color: 'rgb(125, 76, 236)' }}
            compact
            textColor="rgb(125, 76, 236)"
          >
            Sil
          </Button>
        </View>
      </Card.Content>
    </Card>
  );

  return (
    <View style={styles.container}>
      <Searchbar
        placeholder="Ürün Ara..."
        onChangeText={setSearchQuery}
        value={searchQuery}
        style={styles.searchbar}
      />

      {loading && !refreshing ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="rgb(125, 76, 236)" />
          <Text style={styles.loadingText}>Ürünler yükleniyor...</Text>
        </View>
      ) : filteredItems.length === 0 ? (
        <View style={styles.emptyContainer}>
          <MaterialCommunityIcons name="food-off" size={80} color="rgb(125, 76, 236)" />
          <Text style={styles.emptyText}>Bu kategoride henüz ürün eklenmemiş</Text>
          <Button 
            mode="contained" 
            onPress={() => navigation.navigate('MenuItemForm', { categoryId })}
            style={[styles.addButton, {backgroundColor: 'rgb(125, 76, 236)'}]}
            labelStyle={{color: 'white'}}
          >
            <Text style={{color: 'white'}}>Yeni Ürün Ekle</Text>
          </Button>
        </View>
      ) : (
        <FlatList
          data={filteredItems}
          keyExtractor={(item) => item.id.toString()}
          renderItem={renderMenuItem}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['rgb(125, 76, 236)']} />
          }
        />
      )}

      <FAB
        style={styles.fab}
        icon="plus"
        onPress={() => navigation.navigate('MenuItemForm', { categoryId })}
        color="white"
      />

      <Portal>
        <Dialog visible={deleteDialogVisible} onDismiss={() => setDeleteDialogVisible(false)}>
          <Dialog.Title style={{color: 'rgb(125, 76, 236)'}}>Ürün Silme</Dialog.Title>
          <Dialog.Content>
            <Paragraph>
              "{itemToDelete?.name}" ürününü silmek istediğinizden emin misiniz?
              Bu işlem geri alınamaz.
            </Paragraph>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeleteDialogVisible(false)} textColor="rgb(125, 76, 236)">İptal</Button>
            <Button onPress={handleDeleteItem} textColor="rgb(125, 76, 236)" style={{backgroundColor: 'rgba(125, 76, 236, 0.1)'}}>Sil</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        style={{backgroundColor: 'rgb(125, 76, 236)'}}
        theme={{colors: {surface: '#fff'}}}
        action={{
          label: 'Tamam',
          onPress: () => setSnackbarVisible(false),
          color: '#fff'
        }}
      >
        {snackbarMessage}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  searchbar: {
    marginHorizontal: 16,
    marginVertical: 8,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
  },
  listContent: {
    padding: 8,
  },
  card: {
    marginVertical: 6,
    marginHorizontal: 8,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  itemHeader: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  itemImage: {
    width: 80,
    height: 80,
    borderRadius: 8,
  },
  imagePlaceholder: {
    width: 80,
    height: 80,
    borderRadius: 8,
    backgroundColor: '#EEEEEE',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#DDDDDD',
    borderStyle: 'dashed',
  },
  itemInfo: {
    marginLeft: 16,
    flex: 1,
  },
  itemName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  itemPrice: {
    fontSize: 16,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
    marginTop: 4,
  },
  itemDescription: {
    marginBottom: 12,
    color: '#757575',
  },
  statusChip: {
    marginTop: 6,
    height: 26,
    alignSelf: 'flex-start',
  },
  itemActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  actionButton: {
    marginLeft: 8,
  },
  deleteButton: {
    borderColor: 'red',
  },
  fab: {
    position: 'absolute',
    margin: 16,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgb(125, 76, 236)',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  emptyText: {
    fontSize: 18,
    marginTop: 16,
    marginBottom: 24,
    color: '#757575',
  },
  addButton: {
    paddingHorizontal: 8,
  },
});

export default MenuItemListScreen;
